import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { ArrowLeft, Gift, CreditCard, CheckCircle2 } from 'lucide-react';
import ExitSurveyPrompt from '@/components/ExitSurveyPrompt';
import { TRIAL_DAYS, ORGANIZATION_PLAN } from '@/config/tierPricing';

export default function PaymentOptionStep({ tier, selected, onBack, onSelect }) {
  // Under the new pricing, organization is the only paid org plan.
  // Fall back to ORGANIZATION_PLAN if `tier` wasn't passed in.
  const tierName = tier?.name || ORGANIZATION_PLAN.name;
  const priceSgd = tier?.priceSgd ?? ORGANIZATION_PLAN.onboardingSgd;
  const totalPrice = priceSgd; // SGD 499/yr — flat, no units multiplier

  return (
    <>
      <CardHeader>
        <Button variant="ghost" size="sm" onClick={onBack} className="w-fit -ml-2 mb-2" data-testid="payment-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <CardTitle>How would you like to get started?</CardTitle>
        <CardDescription>
          Choose to try SAFER-i™ free for {TRIAL_DAYS} days or pay now to start immediately
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Trial Option */}
        <button
          onClick={() => onSelect('trial')}
          className={`w-full p-5 border-2 rounded-xl text-left transition-all hover:border-emerald-500 ${
            selected === 'trial' ? 'border-emerald-500 bg-emerald-50/40' : ''
          }`}
          data-testid="trial-option"
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
              <Gift className="w-6 h-6 text-emerald-600" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-lg">Start with {TRIAL_DAYS}-Day Free Trial</h3>
                <Badge className="bg-emerald-500 text-white text-xs">Recommended</Badge>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Full access to all {tierName} features for {TRIAL_DAYS} days. No payment required upfront.
              </p>
              <div className="flex items-center gap-4 mt-3 text-xs text-slate-600">
                <span className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> Full feature access</span>
                <span className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> No credit card needed</span>
                <span className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> Cancel anytime</span>
              </div>
              <p className="text-xs text-amber-600 mt-2 font-medium">
                Account will be suspended after {TRIAL_DAYS} days unless payment is made.
              </p>
            </div>
          </div>
        </button>

        {/* Pay Now Option */}
        <button
          onClick={() => onSelect('pay_now')}
          className={`w-full p-5 border-2 rounded-xl text-left transition-all hover:border-indigo-500 ${
            selected === 'pay_now' ? 'border-indigo-500 bg-indigo-50/40' : ''
          }`}
          data-testid="pay-now-option"
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center flex-shrink-0">
              <CreditCard className="w-6 h-6 text-indigo-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-lg">Pay Now & Start Immediately</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Complete payment via Stripe and activate your {tierName} plan instantly.
              </p>
              <div className="flex items-center justify-between mt-3">
                <div className="flex items-center gap-4 text-xs text-slate-600">
                  <span className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-indigo-500" /> Instant activation</span>
                  <span className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-indigo-500" /> Secure Stripe checkout</span>
                </div>
                <span className="font-bold text-primary">
                  SGD ${totalPrice.toLocaleString(undefined, { minimumFractionDigits: 2 })}/yr
                </span>
              </div>
            </div>
          </div>
        </button>

        <div className="bg-slate-50 rounded-lg p-3 text-sm">
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">Selected Plan</span>
            <span className="font-medium">{tierName}</span>
          </div>
        </div>

        {/* Anonymous "what stopped you?" survey — auto-shows after 25s on this
            step (matches the 84.6% drop-off observed in the funnel data). */}
        <ExitSurveyPrompt
          kind="signup"
          step="payment_option"
          delaySeconds={25}
          className="mt-2"
        />
      </CardContent>
    </>
  );
}
